'use client';

import { createContext, useContext, useState, useCallback, useRef, useEffect, type ReactNode } from 'react';
import type { LocalAgentStatus, LocalTool } from '@/lib/local-agent/types';

interface LocalAgentContextValue {
  status: LocalAgentStatus;
  isReady: boolean;
  isAvailable: boolean;
  initialize: (onProgress?: (p: { text: string; progress: number }) => void) => Promise<void>;
  run: (message: string) => Promise<string>;
  runStreaming: (message: string, onDelta: (text: string) => void) => Promise<string>;
  cleanup: () => Promise<void>;
  addTool: (tool: LocalTool) => void;
}

const DEFAULT_STATUS: LocalAgentStatus = {
  ready: false,
  loading: false,
  modelLoaded: false,
  modelId: null,
  error: null,
  gpuAvailable: false,
};

const LocalAgentContext = createContext<LocalAgentContextValue>({
  status: DEFAULT_STATUS,
  isReady: false,
  isAvailable: false,
  initialize: async () => {},
  run: async () => '',
  runStreaming: async () => '',
  cleanup: async () => {},
  addTool: () => {},
});

export function useLocalAgentContext(): LocalAgentContextValue {
  return useContext(LocalAgentContext);
}

export function LocalAgentProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<LocalAgentStatus>(() => ({
    ...DEFAULT_STATUS,
    gpuAvailable: typeof navigator !== 'undefined' && 'gpu' in navigator,
  }));
  const runtimeRef = useRef<any>(null);
  const extraTools = useRef<LocalTool[]>([]);

  const initialize = useCallback(async (onProgress?: (p: { text: string; progress: number }) => void) => {
    if (runtimeRef.current?.getStatus().ready) return;

    const { createDefaultLocalAgent } = await import('@/lib/local-agent/runtime');
    const runtime = createDefaultLocalAgent({
      tools: extraTools.current,
    });
    runtimeRef.current = runtime;

    runtime.onStatusChange(setStatus);
    await runtime.initialize(onProgress);
  }, []);

  const run = useCallback(async (message: string): Promise<string> => {
    const runtime = runtimeRef.current;
    if (!runtime || !runtime.getStatus().ready) {
      throw new Error('Local agent not ready');
    }
    return runtime.run([{ role: 'user', content: message }]);
  }, []);

  const runStreaming = useCallback(async (message: string, onDelta: (text: string) => void): Promise<string> => {
    const runtime = runtimeRef.current;
    if (!runtime || !runtime.getStatus().ready) {
      throw new Error('Local agent not ready');
    }

    let fullText = '';
    for await (const chunk of runtime.runStreaming([{ role: 'user', content: message }])) {
      if (chunk.type === 'delta' && chunk.text) {
        fullText += chunk.text;
        onDelta(chunk.text);
      }
    }
    return fullText;
  }, []);

  const cleanup = useCallback(async () => {
    await runtimeRef.current?.cleanup();
    runtimeRef.current = null;
  }, []);

  const addTool = useCallback((tool: LocalTool) => {
    extraTools.current.push(tool);
  }, []);

  useEffect(() => {
    return () => {
      runtimeRef.current?.cleanup();
    };
  }, []);

  return (
    <LocalAgentContext.Provider value={{
      status,
      isReady: status.ready,
      isAvailable: status.gpuAvailable,
      initialize,
      run,
      runStreaming,
      cleanup,
      addTool,
    }}>
      {children}
    </LocalAgentContext.Provider>
  );
}
